// @flow

import React from 'react';
import { get } from 'lodash';

import ErrorInfo from 'app/common/components/ErrorInfo';
import Errors from 'app/errors';
import InlineUpgradeTriggerModal from 'app/beta-editor/components/InlineUpgradeTrigger/InlineUpgradeTriggerContainer';
import LazyLoadDestinationAuthModal from 'app/common/components/LazyLoadDestinationAuthModal';
import LearnMoreErrorInfo from 'app/common/components/LearnMoreErrorInfo';
import Modal from 'app/common/components/Modal';
import UnpauseErrorModal from 'app/common/components/UnpauseErrorModal';
import UpgradeModal from 'app/common/components/UpgradeModal';
import { isDataFlowProtectionRequired } from 'app/common/utils/ErrorUtils';
import { selectErrorMessages } from 'app/common/error';

type ErrorModalProps = {
  onClose: () => void,
  errors: Array<Object>,
  isFromContactForm?: boolean,
};

const ErrorModal = ({
  onClose,
  errors,
  isFromContactForm,
}: ErrorModalProps) => {
  let error = errors[errors.length - 1];

  if (error && error.status === 426 && error.errors[0]) {
    const plans = get(error, ['errors', '0', 'plans']);
    const upgradeToPlan = get(plans, 'annual') || get(plans, 'monthly');

    if (upgradeToPlan) {
      return (
        <InlineUpgradeTriggerModal
          autoOpen={true}
          flowId={get(error, ['flowId'])}
          onClose={onClose}
          reason={get(error, ['errors', '0', 'reasons', '0', 'extension'])}
          upgradeToPlan={upgradeToPlan}
        />
      );
    } else {
      return (
        <UpgradeModal
          onCloseModal={onClose}
          reasons={error.errors[0].reasons}
        />
      );
    }
  }

  if (isDataFlowProtectionRequired(errors)) {
    return (
      <LazyLoadDestinationAuthModal
        checks={(error.json || {}).checks}
        flowId={error.flowId}
        onCloseModal={onClose}
      />
    );
  }

  if (
    error &&
    error.status === 403 &&
    error.name === 'UnpauseError' &&
    error.flowId
  ) {
    return (
      <UnpauseErrorModal
        checks={(error.json || {}).checks}
        flowId={error.flowId}
        onCloseModal={onClose}
      />
    );
  }

  let ErrorType = error && error.name && Errors[error.name];

  if (!ErrorType) {
    error = new Errors.UnknownError(error);
    ErrorType = Errors.UnknownError;
  }

  const summary = ErrorType.summary(error);
  const errorsFound = ErrorType.errors(error);
  const messages = selectErrorMessages(errorsFound);

  const content = ErrorType.hasHelpLink(error) ? (
    <LearnMoreErrorInfo
      continueText="or continue to Dashboard"
      helpLink={ErrorType.helpLink(error)}
      onClose={onClose}
      summary={ErrorType.summary(error)}
      title="There's an Error With This Zap"
    />
  ) : (
    <ErrorInfo
      isFromContactForm={isFromContactForm}
      messages={messages}
      status={error.status}
      summary={summary}
    />
  );

  return (
    <Modal
      closeOnEsc={true}
      closeOnOutsideClick={true}
      isOpened={true}
      onClose={onClose}
    >
      {content}
    </Modal>
  );
};

export default ErrorModal;
